/* ──────────────────────────────────────────────────────────────
 *  3-D geometry preview types
 * ────────────────────────────────────────────────────────────── */

/** Tessellated triangle mesh for a single component (core / MRE / sleeve). */
export interface ComponentMesh {
  /** Flat array of vertex coordinates [x0,y0,z0, x1,y1,z1, …]. */
  vertices: number[];
  /** Triangle indices [i0,j0,k0, i1,j1,k1, …]. */
  triangles: number[];
  /** Edge polyline coordinates for wireframe overlay. */
  edges: { x: number[]; y: number[]; z: number[] };
  /** Display colour hex (e.g. "#666666" for MRE). */
  color: string;
  /** Component label shown in legend. */
  label: string;
}

/** Complete geometry response for a single sample + view. */
export interface GeometryMesh {
  components: ComponentMesh[];
}

/** Supported 3-D view identifiers. */
export type GeometryView = 'iso' | 'sxh' | 'axh';

/** Flip-state count per bushing type (2 or 3 states). */
export interface FlipConfig {
  views: GeometryView[];
}

/**
 * Flip configuration per bushing type.
 * - A1, C1, BYD EV Solid, BYD EV Voided: 2 states (iso, sxh)
 * - B1: 2 states (iso, sxh)
 * - D1: 3 states (iso, axh, sxh)
 */
export const FLIP_CONFIGS: Record<string, FlipConfig> = {
  A1: { views: ['iso', 'sxh'] },
  B1: { views: ['iso', 'sxh'] },
  C1: { views: ['iso', 'sxh'] },
  D1: { views: ['iso', 'axh', 'sxh'] },
  'BYD EV Voided': { views: ['iso', 'axh', 'sxh'] },
  'BYD EV Solid': { views: ['iso', 'sxh'] },
};

/** Cached geometry artefacts per sample (mirrors Python AppState). */
export interface GeometryCache {
  asm: unknown | null;
  synera: unknown | null;
  marc: unknown | null;
  sxh: unknown | null;
  axh?: unknown | null;
  stp: Blob | null;
  radius: number | null;
  area: number | null;
  pngIso: string | null;
  pngSxh: string | null;
  pngAxh?: string | null;
}
